import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchOngoingCalls, subscribeOngoingCalls, triggerOngoingCalls } from '../../utils/AmplifyOngoingCalls/api';
import { normalizeCall, normalizeVri, nonNull, type OngoingCallView, type VriOngoingView } from '../../models/mappers/ongoingCall';
import type { GetOngoingCallsPagedQueryVariables } from '../../models/amplify/API';

export type UseOngoingCallsOptions = {
  removeIf?: (c: OngoingCallView) => boolean;

  initialPage?: number;
  pageSize?: number;
  filter?: GetOngoingCallsPagedQueryVariables['filter'];
  sortDirection?: GetOngoingCallsPagedQueryVariables['sortDirection'];
};

export function useOngoingCalls(opts: UseOngoingCallsOptions = {}) {
  const {
    removeIf,
    initialPage = 1,          
    pageSize: initialPageSize = 1000,
    filter: initialFilter,
    sortDirection: initialSortDir,
  } = opts;

  const [opiCalls, setOpiCalls] = useState<OngoingCallView[]>([]);
  const [vriCalls, setVriCalls] = useState<VriOngoingView[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);


  const [page, setPage] = useState<number>(initialPage);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [filter, setFilter] = useState<GetOngoingCallsPagedQueryVariables['filter'] | undefined>(initialFilter);
  const [sortDirection, setSortDirection] =
    useState<GetOngoingCallsPagedQueryVariables['sortDirection'] | undefined>(initialSortDir);

  const [isLastPage, setIsLastPage] = useState(false);

  const reqId = useRef(0);

const load = useCallback(async (p = page, ps = pageSize, f = filter, s = sortDirection) => {
  const id = ++reqId.current;
  setLoading(true);
  setErr(null);
  try {
    const raw = await fetchOngoingCalls({ page: p, pageSize: ps, filter: f, sortDirection: s });
    if (reqId.current !== id) return;

    const opiList = (raw.opi ?? []).map(normalizeCall).filter(nonNull);
    const vriList = (raw.vri ?? []).map(normalizeVri).filter(nonNull);

    const visibleOpi = removeIf ? opiList.filter(c => !removeIf(c)) : opiList;
    setOpiCalls(visibleOpi);
    setVriCalls(vriList);

    if (opiList.length < ps) {
      setIsLastPage(true);
    } else {
      const probe = await fetchOngoingCalls({ page: p + 1, pageSize: 1, filter: f, sortDirection: s });
      const hasNext = (probe.opi ?? []).length > 0;
      if (reqId.current === id) setIsLastPage(!hasNext);
    }
  } catch (e: any) {
    setErr(e?.errors?.[0]?.message ?? e?.message ?? 'Failed to load');
  } finally {
    if (reqId.current === id) setLoading(false);
  }
}, [page, pageSize, filter, sortDirection, removeIf]);


  useEffect(() => {
    load();
  }, [page, pageSize, filter, sortDirection]);

  useEffect(() => {
    let active = true;
    const unsubscribe = subscribeOngoingCalls(
      () => {
        if (!active) return;
        load();
      },
      (e) => {
        console.warn('subscription error', e);
      }
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }, [load]);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  const nextPage = useCallback(() => {
    if (!isLastPage) setPage((p) => p + 1);
  }, [isLastPage]);

  const prevPage = useCallback(() => {
    setPage((p) => Math.max((initialPage ?? 1), p - 1));
  }, [initialPage]);

  const goToPage = useCallback((p: number) => setPage(p), []);
  const changePageSize = useCallback((ps: number) => {
    setPageSize(ps);
    setPage(initialPage ?? 1); 
  }, [initialPage]);

  const hasCalls = useMemo(() => opiCalls.length > 0 || vriCalls.length > 0, [opiCalls.length, vriCalls.length]);
  const isFirstPage = useMemo(() => page === (initialPage ?? 1), [page, initialPage]);

  // useEffect(() => {
  //   console.log('__________________________________from useOngoingCalls:');
  //   console.log("pageSize is: ", pageSize);
  //   console.log("page is: ", page);
  //   console.log("isLastPage is: ", isLastPage);
  // }, [page, pageSize, isLastPage]);


  return {
    opiCalls, vriCalls, loading, err, hasCalls,
    page, pageSize, isFirstPage, isLastPage,
    nextPage, prevPage, goToPage, setPage, setPageSize: changePageSize,
    filter, setFilter,
    sortDirection, setSortDirection,
    refresh, trigger: useCallback(async () => {
      setErr(null);
      try {
        await triggerOngoingCalls();
        await load();
      } catch (e: any) {
        setErr(e?.errors?.[0]?.message ?? e?.message ?? 'Mutation failed');
      }
    }, [load]),
  };
}
